/**
 * feat-243 — the 409 conflict, as data: the wire shape, the parse, and the
 * replay derivation. No JSX, no copy, so every rule below is unit-testable
 * without rendering a pane.
 *
 * ── WHY THE INTENT IS DERIVED AND NOT PLUMBED ─────────────────────────────
 * `setMembership` has three callers with three different intents (`addChecked`,
 * `removeLesson`, `onReorderLessons`). The obvious move is to thread a `kind`
 * prop down through `TrackDetailPane` and `LessonLibraryPane` — don't. At the
 * moment of the PUT both halves are already in hand: `submitted` (what was sent)
 * and `expected` (what was loaded). Their difference IS the intent, so the
 * reorder rule becomes a property of the data rather than a string three call
 * sites have to keep truthful, and neither pane grows a prop
 * (`design/CONFLICT-BRIEF.md` §3).
 *
 * ── WHAT THIS IS NOT ──────────────────────────────────────────────────────
 * R2 forbids silently merging the pending edit onto the fresh list and re-PUTting
 * it. `replay` below is COMPUTED for display — the banner says what the button
 * will do before it is pressed — and executed only when the author presses it.
 * Computing it is what makes the explicit replay honest; firing it without a
 * press is the thing R2 rules out.
 */

import { ApiError } from '../../../../utils/api';

/** Which endpoint the conflict came from. Decides set-vs-sequence semantics,
 *  whether a `moved` group can exist, and the whole prereq copy branch. */
export type ConflictScope = 'lessons' | 'prereqs';

/** The `detail` object the 409 carries (spec.md §3a), FastAPI-wrapped under a
 *  top-level `detail` key. Every list is optional here on purpose: this is
 *  parsed from the network, and a missing key must degrade to an empty group
 *  rather than throw inside a render. */
export interface TrackConflictDetail {
  error?: string;
  message?: string;
  current_lesson_ids?: string[];
  expected_lesson_ids?: string[];
  current_required_track_ids?: string[];
  expected_required_track_ids?: string[];
  added?: string[];
  removed?: string[];
  /** Lessons only — prereqs compare as a SET, so they have no notion of moved. */
  moved?: string[];
  /** NULLABLE, and null does NOT mean "someone". It means there is no audit row,
   *  which can mean migration 114's backfill wrote the membership and no person
   *  was involved. The copy's anonymous branch exists for exactly that. */
  changed_by?: string | null;
}

/** One refused write, everything the surface needs, for ONE track. */
export interface TrackConflict {
  /** The track the refusal is about. A conflict does not survive a change of
   *  selection — carrying it to another track would be a lie. */
  trackId: string;
  scope: ConflictScope;
  changedBy: string | null;
  /** THEIR diff, from the server. */
  added: string[];
  removed: string[];
  moved: string[];
  /** The server's current list — what the pane now shows after the refetch. */
  current: string[];
  /** What this client sent. */
  submitted: string[];
  /** What this client believed it was overwriting. */
  expected: string[];
}

/** MY intent, derived from `submitted` vs `expected`, plus what replaying it
 *  against the fresh `current` would produce. */
export interface ReplayIntent {
  myAdded: string[];
  myRemoved: string[];
  /** Same set, different order — exactly and only the drag case, and only
   *  meaningful for lessons. There is no re-apply for it: "move C above B" has
   *  no meaning against a list that changed underneath it. */
  isPureReorder: boolean;
  /** The list a press of "Re-apply my change" would PUT. */
  replay: string[];
  /** The replay would change nothing — the other author already made the same
   *  change. Offering a button that fires a no-op PUT teaches the author the
   *  button is decorative. */
  isNoop: boolean;
  /** Which pill to show, or null when the button shows. */
  suppressed: 'reorder' | 'noop' | null;
}

const asIds = (v: unknown): string[] =>
  Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];

const sameSequence = (a: string[], b: string[]): boolean =>
  a.length === b.length && a.every((id, i) => id === b[i]);

/**
 * Pull the conflict `detail` out of a thrown error, or return null.
 *
 * Deliberately conservative: it is a conflict only when the status is **409**
 * AND the body carries the `error` discriminator this feature defines. Anything
 * else — including some other endpoint's 409, of which `DELETE /learn-tracks/{id}`
 * on the default track is a live one — falls through to `writeError` unchanged.
 */
export function parseTrackConflictDetail(err: unknown): TrackConflictDetail | null {
  if (!(err instanceof ApiError) || err.status !== 409) return null;
  const body = err.body as { detail?: unknown } | null | undefined;
  const detail = body && typeof body === 'object' ? body.detail : undefined;
  if (!detail || typeof detail !== 'object' || Array.isArray(detail)) return null;
  const d = detail as TrackConflictDetail;
  if (d.error !== 'track_membership_conflict' && d.error !== 'track_prereq_conflict') return null;
  return d;
}

/**
 * Build the surface's state from a thrown error plus what this client sent.
 *
 * `current` comes from the SERVER's echo, not from a re-read of props: the
 * refetch is async and the banner must be able to describe the diff the server
 * described, even if the reload is still in flight or itself fails.
 */
export function toTrackConflict(
  err: unknown,
  ctx: { trackId: string; scope: ConflictScope; submitted: string[]; expected: string[] },
): TrackConflict | null {
  const d = parseTrackConflictDetail(err);
  if (!d) return null;
  const current =
    ctx.scope === 'lessons' ? asIds(d.current_lesson_ids) : asIds(d.current_required_track_ids);
  return {
    trackId: ctx.trackId,
    scope: ctx.scope,
    changedBy: typeof d.changed_by === 'string' && d.changed_by ? d.changed_by : null,
    added: asIds(d.added),
    removed: asIds(d.removed),
    // A `moved` group on a prereq conflict would be the server contradicting
    // its own set semantics; drop it rather than render a row that cannot mean
    // anything (spec.md §3b rule 2).
    moved: ctx.scope === 'lessons' ? asIds(d.moved) : [],
    current,
    submitted: ctx.submitted,
    expected: ctx.expected,
  };
}

/**
 * Derive the pending intent and the replay.
 *
 * The replay puts additions at the END of the current list, which is
 * `addChecked`'s documented rule ("new members land at the END, in library
 * order") and therefore the placement the author already saw once.
 */
export function deriveIntent(conflict: TrackConflict): ReplayIntent {
  const { submitted, expected, current, scope } = conflict;
  const myAdded = submitted.filter((id) => !expected.includes(id));
  const myRemoved = expected.filter((id) => !submitted.includes(id));

  // Same set, new order. Only lessons have an order to change — a prereq write
  // whose set is unchanged changed nothing at all, and falls to `isNoop` below
  // with the honest "your change and theirs were the same" reason.
  const isPureReorder = scope === 'lessons' && myAdded.length === 0 && myRemoved.length === 0;

  const replay = [
    ...current.filter((id) => !myRemoved.includes(id)),
    ...myAdded.filter((id) => !current.includes(id)),
  ];
  // Sequence equality is the right test for both scopes GIVEN THIS CONSTRUCTION:
  // `replay` is `current` with removals filtered out and additions appended, so
  // it can only equal `current` element-for-element when neither happened.
  const isNoop = !isPureReorder && sameSequence(replay, current);

  return {
    myAdded,
    myRemoved,
    isPureReorder,
    replay,
    isNoop,
    suppressed: isPureReorder ? 'reorder' : isNoop ? 'noop' : null,
  };
}
